import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../main/Apicall";
import Pagination from "../Reusablecomponents/Pagination";
import ExportButton from "../Reusablecomponents/ExportButton";
import { FaEdit, FaTrash, FaPlus, FaEye, FaFileExport, FaUpload, FaTimes } from "react-icons/fa";
import EditChecklistModal from "../Popups/EditChecklistModal";
import CreateChecklistModal from "../Popups/CreateChecklistModal";
import DeleteConfirmationModal from "../Popups/DeleteConfirmationModal";
import ToastPopup from "../Popups/ToastPopup";
import axios from "axios";
import "../css/others/Table.css";
import * as XLSX from 'xlsx-js-style';
import { createToastMessage, ToastMessage } from "../Interfaces/Interface";
import EncryptionUtils from "../main/encryptionUtils";
const CheckListManagement: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editableDetails, setEditableDetails] = useState<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [newChecklistName, setNewChecklistName] = useState<string>("");
  const [newChecklistDescription, setNewChecklistDescription] = useState<string>("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [isDownloadTemplateModalOpen, setIsDownloadTemplateModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"DRAFT" | "ACTIVE">("DRAFT");
  const [selectedChecklists, setSelectedChecklists] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [isPublishConfirmModalOpen, setIsPublishConfirmModalOpen] = useState<boolean>(false);
  const [publishingChecklistId, setPublishingChecklistId] = useState<string | null>(null);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState<boolean>(false);
  const navigate = useNavigate();
  const [, setUploadError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const modifiedBy = EncryptionUtils.getLocalStorage<string>("email") || "";
  const exportColumns = [{ key: "templateName", label: "CheckList Name" }, { key: "description", label: "Description" }, { key: "checklistType", label: "Checklist Type" }];
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = "";
      if (activeTab === "DRAFT") {
        endpoint = "/api/checklist/templates/by-status";
      } else {
        endpoint = "/api/checklist/templates";
      }
      const params: any = { page: currentPage - 1, size: itemsPerPage };
      if (activeTab === "DRAFT") {
        params.status = "DRAFT";
      }
      const response = await apiCall("get", endpoint, undefined, params);
      const { content, totalElements, totalPages } = response;
      setData(content);
      setTotalElements(totalElements);
      setTotalPages(totalPages);
      setSelectedChecklists([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, activeTab]);
  useEffect(() => { fetchData(); }, [fetchData]);
  const fetchAllDataForExport = useCallback(async () => {
    try {
      let endpoint = "";
      if (activeTab === "DRAFT") {
        endpoint = "/api/checklist/templates/by-status";
      } else {
        endpoint = "/api/checklist/templates";
      }
      const params: any = { page: 0, size: totalElements };
      if (activeTab === "DRAFT") {
        params.status = "DRAFT";
      }
      const response = await apiCall("get", endpoint, undefined, params);
      return response.content?.map((row: any) => ({
        templateName: row.templateName, description: row.description, checklistType: row.isIntegratedChecklist ? "Integrated Checklist" : "Normal Checklist"
      })) || [];
    } catch (error) {
      console.error("Error exporting data:", error);
      return [];
    }
  }, [totalElements, activeTab]);
  const handleSelectChecklist = (id: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedChecklists((prev) => [...prev, id]);
    } else {
      setSelectedChecklists((prev) => prev.filter((item) => item !== id));
      setSelectAll(false);
    }
  };
  const handleSelectAll = (isChecked: boolean) => {
    setSelectAll(isChecked);
    if (isChecked) {
      const allIds = data.map((item) => item.templateId);
      setSelectedChecklists(allIds);
    } else {
      setSelectedChecklists([]);
    }
  };
  const handlePublishSelected = async () => {
    if (selectedChecklists.length === 0) {
      setMessage(createToastMessage("No checklists selected.", "warning", 3000));
      return;
    }
    const checklistsWithoutQuestions: string[] = [];
    for (const templateId of selectedChecklists) {
      try {
        const response = await apiCall("get", `/api/checklist/templates/${templateId}/questions/edit`, null, { page: 0, size: 1 });
        const hasQuestions = response.content && response.content.length > 0;
        if (!hasQuestions) {
          const checklist = data.find(d => d.templateId === templateId);
          checklistsWithoutQuestions.push(checklist?.templateName || templateId);
        }
      } catch {
        const checklist = data.find(d => d.templateId === templateId);
        checklistsWithoutQuestions.push(checklist?.templateName || templateId);
      }
    }
    if (checklistsWithoutQuestions.length > 0) {
      setMessage(createToastMessage(`Please add questions for: ${checklistsWithoutQuestions.join(", ")}`, "warning", 3000));
      return;
    }
    try {
      for (const templateId of selectedChecklists) {
        await apiCall("post", `/api/checklist/templates/${templateId}/publish`);
      }
      setMessage(createToastMessage("Checklists published successfully!", "success", 3000));
      fetchData();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to publish selected checklists";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const confirmPublish = async () => {
    if (publishingChecklistId) {
      try {
        const response = await apiCall("get", `/api/checklist/templates/${publishingChecklistId}/questions/edit`, null, { page: 0, size: 1 });
        const hasQuestions = response.content && response.content.length > 0;
        if (!hasQuestions) {
          setMessage(createToastMessage("Please add questions before publishing.", "warning", 3000));
          setIsPublishConfirmModalOpen(false);
          setPublishingChecklistId(null);
          return;
        }
        await apiCall("post", `/api/checklist/templates/${publishingChecklistId}/publish`);
        setMessage(createToastMessage("Checklist published successfully!", "success", 3000));
        fetchData();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Failed to publish checklist";
        setMessage(createToastMessage(errorMessage, "error", 3000));
      } finally {
        setIsPublishConfirmModalOpen(false);
        setPublishingChecklistId(null);
      }
    }
  };
  const handleAddQuestions = async (templateId: string, isIntegratedChecklist: boolean, controls: string[]) => {
    setIsLoadingQuestions(true);
    try {
      navigate(`/checklistdetails/${templateId}`, { state: { templateId: templateId, isIntegratedChecklist, controls, status: activeTab } });
    } catch (error: any) {
      console.error("Error navigating:", error);
      setMessage(createToastMessage("Failed to open checklist for editing", "error", 3000));
    } finally {
      setIsLoadingQuestions(false);
    }
  };
  const handleViewDetails = (templateId: string, isIntegratedChecklist: boolean, controls: string[]) => {
    navigate(`/viewchecklistdetails/${templateId}`, { state: { templateId, isIntegratedChecklist, controls } });
  };
  const handleEditClick = (row: any) => {
    setEditableDetails({ ...row, controls: row.controls || [], scope: row.scope || "" });
    setIsEditModalOpen(true);
  };
  const handleDelete = (templateId: string) => {
    setDeleteTemplateId(templateId);
    setIsDeleteModalOpen(true);
  };
  const confirmDelete = async () => {
    if (deleteTemplateId) {
      try {
        await apiCall("delete", `/api/checklist/templates/${deleteTemplateId}`);
        setMessage(createToastMessage("Checklist deleted successfully!", "success", 3000));
        fetchData();
      } catch (error) {
        setMessage(createToastMessage(`Failed to delete checklist: ${error instanceof Error ? error.message : String(error)}`, "error", 3000));
      } finally {
        setIsDeleteModalOpen(false);
        setDeleteTemplateId(null);
      }
    }
  };
  const handleSaveChanges = async () => {
    if (editableDetails) {
      try {
        const updateData: any = { templateName: editableDetails.templateName, description: editableDetails.description, modifiedBy: modifiedBy };
        if (editableDetails.isIntegratedChecklist) {
          updateData.controls = editableDetails.controls || [];
          updateData.scope = "";
        } else {
          updateData.scope = editableDetails.scope || "";
        }
        await apiCall("put", `/api/checklist/templates/${editableDetails.templateId}`, updateData);
        setMessage(createToastMessage("Checklist updated successfully!", "success", 3000));
        setIsEditModalOpen(false);
        fetchData();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "Failed to update checklist";
        setMessage(createToastMessage(errorMessage, "error", 3000));
      }
    }
  };
  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewChecklistName("");
    setNewChecklistDescription("");
  };
  const handleSaveNewChecklist = async (newChecklistData: { templateName: string; description: string; isIntegratedChecklist: boolean; controls: string[]; scope: string }) => {
    try {
      const dataToSend = { templateName: newChecklistData.templateName, description: newChecklistData.description, isIntegratedChecklist: newChecklistData.isIntegratedChecklist, controls: newChecklistData.isIntegratedChecklist ? newChecklistData.controls : [], scope: !newChecklistData.isIntegratedChecklist ? newChecklistData.scope : "", createdBy: modifiedBy };
      await apiCall("post", "/api/checklist/templates", dataToSend);
      setMessage(createToastMessage("Checklist created successfully!", "success", 3000));
      setIsCreateModalOpen(false);
      setNewChecklistName("");
      setNewChecklistDescription("");
      fetchData();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to create checklist";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const handleExportChecklist = async (templateId: string, templateName: string, isIntegratedChecklist: boolean) => {
    try {
      const endpoint = activeTab === "DRAFT" ? `/api/checklist/templates/${templateId}/questions/edit` : `/api/checklist/templates/${templateId}/questions`;
      const response = await apiCall("get", endpoint, null, { page: 0, size: 2_147_483_647 });
      const questions = response.content || [];
      if (questions.length === 0) {
        setMessage(createToastMessage("No questions available to export.", "warning", 3000));
        return;
      }
      const baseFields: { [key: string]: string } = { questionText: "Questions", expectedArtifact: "Expected Artifact" };
      if (!isIntegratedChecklist) {
        baseFields.practiceArea = "Control/Practice Area";
      }
      const exportData = questions.map((q: { [key: string]: any }) => {
        const row: { [key: string]: any } = {};
        for (const field in baseFields) {
          row[baseFields[field]] = q[field] ?? "";
        }
        if (isIntegratedChecklist) {
          const integratedFields: { [key: string]: string } = { pims: "PIMS", isms: "ISMS", bcms: "BCMS", cmmi: "CMMI", soc1: "SOC 1", soc2: "SOC 2", qms: "QMS", aims: "AIMS", erm: "ERM", itsm: "ITSM", ers: "ERS", ohs: "OHS" };
          for (const field in integratedFields) {
            if (q[field] && q[field].trim() !== "") {
              row[integratedFields[field]] = q[field];
            }
          }
        }
        return row;
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Checklist Questions");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${templateName}_Questions.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setMessage(createToastMessage("Checklist exported successfully!", "success", 3000));
    } catch (error: any) {
      setMessage(createToastMessage(`Failed to export checklist questions: ${error instanceof Error ? error.message : String(error)}`, "error", 3000));
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setUploadError("");
    }
  };
  const handleBulkUpload = async () => {
    if (!file) {
      setMessage(createToastMessage("Please choose a file before uploading.", "error", 3000));
      return;
    }
    try {
      const presignedUrlResponse = await apiCall("get", `/api/bulk-upload/s3presigneduploadurl?fileName=${file.name}&action=mgmt_checklist_creation`);
      const presignedUrl = presignedUrlResponse;
      if (!presignedUrl) {
        throw new Error("Failed to retrieve presigned upload URL.");
      }
      await axios.put(presignedUrl, file, { headers: { "Content-Type": file.type } });
      setMessage(createToastMessage("File uploaded successfully!", "success", 3000));
      const urlObj = new URL(presignedUrl);
      const fileKey = decodeURIComponent(urlObj.pathname.substring(1));
      await apiCall("post", "/api/bulk-upload/complete", { fileKey });
      await apiCall("get", "/api/bulk-upload/process-messages");
      setMessage(createToastMessage("File processed successfully!", "success", 2500));
      setFile(null);
      setTimeout(() => { window.location.reload() }, 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage || "Error during bulk upload", "error", 3000));
    }
  };
  const handleDownloadTemplate = async (templateType: 'normal' | 'integrated') => {
    try {
      const fileName = templateType === 'normal' ? "checklist_template.xlsx" : "integrated_checklist_template.xlsx";
      const response = await apiCall("get", "/api/bulk-upload/s3presigneddownloadurl", null, { fileName, action: "default" });
      const presignedUrl = response;
      if (!presignedUrl) throw new Error("Failed to retrieve presigned URL");
      const fileResponse = await axios.get(presignedUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage(createToastMessage(`${templateType === 'normal' ? 'Normal' : 'Integrated'} template downloaded successfully.`, "success", 3000));
      setIsDownloadTemplateModalOpen(false);
    } catch (error) {
      setMessage(createToastMessage("Failed to download the template.", "error", 3000));
    }
  };
  const triggerFileInput = () => { fileInputRef.current?.click(); };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Checklists</h2>
        <div className="table-title-actions">
          <button className="create-checklist" onClick={handleOpenCreateModal}>Create Checklist</button>
          <button className="download-template" onClick={() => setIsDownloadTemplateModalOpen(true)}>Download Checklist template</button>
          <button type="button" className={`upload-checklist${file ? " disabled" : ""}`} onClick={triggerFileInput} disabled={!!file}>Upload Checklist</button>
          <input ref={fileInputRef} type="file" accept=".csv, .xlsx, .json" onChange={handleFileChange} className="hidden-input" disabled={!!file} />
          {file && (
            <button onClick={handleBulkUpload} className="upload-file"> <FaUpload />Upload File</button>
          )}
          {activeTab === "DRAFT" && (
            <button className="publish" onClick={handlePublishSelected}>Publish</button>
          )}
          <ExportButton columns={exportColumns} fileName={`CheckLists_${activeTab}`} fetchAllData={fetchAllDataForExport} data={[]} />
        </div>
      </div>
      <div className="tabs-container">
        <button className={`tab-button ${activeTab === "DRAFT" ? "active" : ""}`} onClick={() => setActiveTab("DRAFT")}>Draft Checklists</button>
        <button className={`tab-button ${activeTab === "ACTIVE" ? "active" : ""}`} onClick={() => setActiveTab("ACTIVE")}>Active Checklists</button>
      </div>
      {loading ? (
        <div className="spinner">Loading...</div>
      ) : (
        <>
          <div className="table-container">
            <div className="table-inner-container">
              <table className="main-table">
                <thead>
                  <tr>
                    {activeTab === "DRAFT" && <th><input type="checkbox" checked={selectAll} onChange={(e) => handleSelectAll(e.target.checked)} /></th>}
                    <th>S.No.</th>
                    <th>CheckList Name</th>
                    <th>Description</th>
                    <th>Checklist Type</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(data) && data.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === "DRAFT" ? 6 : 5} className="no-data">No data available</td>
                    </tr>
                  ) : (
                    data.map((row, index) => (
                      <tr key={row.templateId}>
                        {activeTab === "DRAFT" && (
                          <td>
                            <input type="checkbox" checked={selectedChecklists.includes(row.templateId)} onChange={(e) => handleSelectChecklist(row.templateId, e.target.checked)} />
                          </td>
                        )}
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>{row.templateName || "N/A"}</td>
                        <td>{row.description || "N/A"}</td>
                        <td>{row.isIntegratedChecklist ? "Integrated Checklist" : "Normal Checklist"}</td>
                        <td>
                          {activeTab === "ACTIVE" && (
                            <FaEye className="action-icon" onClick={() => handleViewDetails(row.templateId, row.isIntegratedChecklist, row.controls || [])} title="View Questions" />
                          )}
                          <FaPlus className="action-icon" onClick={() => handleAddQuestions(row.templateId, row.isIntegratedChecklist, row.controls || [])} title="Add Questions" style={{ cursor: isLoadingQuestions ? 'wait' : 'pointer' }} />
                          {activeTab === "DRAFT" && (
                            <>
                              <FaEdit className="action-icon" onClick={() => handleEditClick(row)} title="Edit Checklist" />
                              <FaTrash className="action-icon" onClick={() => handleDelete(row.templateId)} title="Delete Checklist" />
                            </>
                          )}
                          <FaFileExport className="action-icon" onClick={() => handleExportChecklist(row.templateId, row.templateName, row.isIntegratedChecklist)} title="Export Questions" />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </>
      )}
      <EditChecklistModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        editableDetails={editableDetails}
        setEditableDetails={setEditableDetails}
        onSave={handleSaveChanges}
      />
      <CreateChecklistModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        newChecklistName={newChecklistName}
        setNewChecklistName={setNewChecklistName}
        newChecklistDescription={newChecklistDescription}
        setNewChecklistDescription={setNewChecklistDescription}
        onSaveNewChecklist={handleSaveNewChecklist}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />
      {isPublishConfirmModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Are you sure you want to publish this checklist?</h3>
            <p>This will make the checklist active and available for use.</p>
            <div className="buttons">
              <button className="cancelbutton" onClick={() => setIsPublishConfirmModalOpen(false)}>Cancel</button>
              <button className="confirmbutton" onClick={confirmPublish}>Yes, Publish</button>
            </div>
          </div>
        </div>
      )}
      {isDownloadTemplateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="export-modal-header">
              <h3>Download Template</h3>
              <button className="close-button" onClick={() => setIsDownloadTemplateModalOpen(false)}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <div className="template-options">
                <button className="normal-checklist" onClick={() => handleDownloadTemplate('normal')}>Normal Checklist Template</button>
                <button className="integrated-checklist" onClick={() => handleDownloadTemplate('integrated')}>Integrated Checklist Template</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default CheckListManagement;